import { useCapabilities } from '@/features/authentication/hooks/useCapabilities';
import { useAuthStore } from '@/features/authentication/stores/authStore';
import { AuthenticatedUploadScope } from '@/features/uploads/components/AuthenticatedUploadScope';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

/**
 * Owns browser upload runtime for the authenticated UID, independently of any route or dialog.
 * Route changes therefore replace only the view; sign-out/account switches tear down the old
 * user's network work, Worker connection and browser-local projection before activating the next.
 */
export function AppSessionBoundary({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const {
    hasCapability,
    isError: capabilitiesError,
    isPending: capabilitiesPending,
  } = useCapabilities();
  const observedUid = user?.uid ?? null;
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const initialized = useRef(false);

  useLayoutEffect(() => {
    if (status === 'loading') return;
    if (!initialized.current) initialized.current = true;
    if (activeUid === observedUid) return;
    setActiveUid(observedUid);
  }, [activeUid, observedUid, status]);

  const switchingIdentity =
    status !== 'loading' && initialized.current && activeUid !== observedUid;
  if (switchingIdentity) return null;

  const uploadsAuthorized =
    !capabilitiesPending &&
    !capabilitiesError &&
    (hasCapability('submissions.local-upload') || hasCapability('source-imports.create'));
  const uploadUid = user?.emailVerified && uploadsAuthorized ? activeUid : null;
  return <AuthenticatedUploadScope uid={uploadUid}>{children}</AuthenticatedUploadScope>;
}
